import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './OperatorSchedules.css';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import { formatAppDate, localToday } from '../../utils/dateUtils';
import { useMessages } from '../../components/Messages';
import { translateBackendMessage } from '../../utils/backendMessages';

const OperatorSchedules = ({ editMode: parentEditMode, onEditModeChange }) => {
    const { t } = useTranslation('admin');
    const { t: tCommon } = useTranslation('common');
    const { showError } = useMessages();
    const [schedules, setSchedules] = useState([]);
    const [operators, setOperators] = useState([]);
    const [machines, setMachines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const editMode = parentEditMode || false;
    const setEditMode = onEditModeChange || (() => {});
    const [editingSchedule, setEditingSchedule] = useState(null);
    const [newSchedule, setNewSchedule] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);

    // Filters
    const [filters, setFilters] = useState({
        startDate: '',
        endDate: '',
        operatorId: '',
        scheduleType: ''
    });

    const scheduleTypes = [
        'SCHEDULED',
        'ACTIVE',
        'COMPLETED',
        'ABSENT',
        'ON_BREAK'
    ];

    // Status codes are data; show them translated (#354)
    const statusKeys = {
        SCHEDULED: 'scheduled',
        ACTIVE: 'active',
        COMPLETED: 'completed',
        ABSENT: 'absent',
        ON_BREAK: 'onBreak'
    };
    const statusLabel = (status) => (statusKeys[status] ? t(`schedules.status.${statusKeys[status]}`) : status);

    useEffect(() => {
        fetchOperators();
        fetchMachines();
    }, []);

    useEffect(() => {
        fetchSchedules();
    }, [filters]);

    // Handle ESC key
    useEffect(() => {
        const handleEscKey = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                if (editingSchedule || newSchedule) {
                    handleCancelEdit();
                } else if (editMode) {
                    setEditMode(false);
                }
            }
        };

        window.addEventListener('keydown', handleEscKey);
        return () => window.removeEventListener('keydown', handleEscKey);
    }, [editMode, editingSchedule, newSchedule]);

    // Set up window handlers for toolbar buttons
    useEffect(() => {
        window.newScheduleHandler = handleNewSchedule;
        window.scheduleSaveHandler = handleSave;

        return () => {
            delete window.newScheduleHandler;
            delete window.scheduleSaveHandler;
        };
    }, [saving, newSchedule, editingSchedule]);

    // Expose toolbar for parent to use
    useEffect(() => {
        if (window.setSchedulesToolbar) {
            const toolbar = !editMode ? (
                <>
                    <button
                        onClick={handleNewSchedule}
                        className="icon-btn"
                        title={t('schedules.toolbar.newSchedule')}
                    >
                        ➕
                    </button>
                    <button
                        onClick={() => setEditMode(true)}
                        className="icon-btn"
                        title={t('schedules.toolbar.editMode')}
                    >
                        ✏️
                    </button>
                </>
            ) : (
                <>
                    <button
                        onClick={handleSave}
                        className="icon-btn"
                        disabled={saving}
                        title={t('schedules.toolbar.saveChanges')}
                    >
                        💾
                    </button>
                    <button
                        onClick={() => {
                            setEditMode(false);
                            setEditingSchedule(null);
                            setNewSchedule(null);
                        }}
                        className="icon-btn"
                        title={t('schedules.toolbar.cancel')}
                    >
                        ❌
                    </button>
                </>
            );
            window.setSchedulesToolbar(toolbar);
        }
    }, [editMode, saving, newSchedule, editingSchedule]);

    const fetchOperators = async () => {
        try {
            // Only active users with the OPERATOR role (#354)
            const response = await apiFetch(getApiUrl('/api/users/?role=OPERATOR&is_active=true&limit=1000'));
            if (response.ok) {
                const data = await response.json();
                setOperators(Array.isArray(data) ? data : []);
            } else {
                showError(tCommon('errors.genericTitle'), tCommon('errors.usersLoadFailed'));
            }
        } catch (error) {
            console.error('Error fetching operators:', error);
            showError(tCommon('errors.genericTitle'), tCommon('errors.usersLoadFailed'));
        }
    };

    const fetchMachines = async () => {
        try {
            const response = await apiFetch(getApiUrl('/api/machines?limit=500'));
            if (response.ok) {
                const data = await response.json();
                setMachines(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error('Error fetching machines:', error);
        }
    };

    const operatorName = (operator) =>
        [operator.first_name, operator.last_name].filter(Boolean).join(' ') || operator.username;

    const fetchSchedules = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (filters.startDate) params.append('start_date', filters.startDate);
            if (filters.endDate) params.append('end_date', filters.endDate);
            if (filters.operatorId) params.append('user_id', filters.operatorId);
            if (filters.scheduleType) params.append('status', filters.scheduleType);

            const response = await apiFetch(getApiUrl(`/api/schedules/?${params}`));

            if (response.ok) {
                const data = await response.json();
                setSchedules(Array.isArray(data) ? data : []);
            } else {
                setError(t('schedules.error.loadFailed'));
            }
        } catch (error) {
            console.error('Error fetching schedules:', error);
            setError(t('schedules.error.loadError'));
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (saving) return;

        try {
            setSaving(true);
            setSaveSuccess(false);

            if (newSchedule) {
                // Create new schedule
                const response = await apiFetch(getApiUrl('/api/schedules/'), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(newSchedule)
                });

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(translateBackendMessage(errorData.detail || errorData.message) || t('schedules.error.createFailed'));
                }

                setSaveSuccess(true);
                setNewSchedule(null);
                setEditMode(false);
                fetchSchedules();

                setTimeout(() => setSaveSuccess(false), 3000);
            } else if (editingSchedule) {
                // Update existing schedule
                const response = await apiFetch(
                    getApiUrl(`/api/schedules/${editingSchedule.schedule_id}`),
                    {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(editingSchedule)
                    }
                );

                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(translateBackendMessage(errorData.detail || errorData.message) || t('schedules.error.updateFailed'));
                }

                setSaveSuccess(true);
                setEditingSchedule(null);
                fetchSchedules();

                setTimeout(() => setSaveSuccess(false), 3000);
            }
        } catch (error) {
            alert(`${t('schedules.error.saveError')}: ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (scheduleId) => {
        if (!window.confirm(t('schedules.messages.deleteConfirm'))) return;

        try {
            const response = await apiFetch(
                getApiUrl(`/api/schedules/${scheduleId}`),
                { method: 'DELETE' }
            );

            if (response.ok) {
                fetchSchedules();
            } else {
                alert(t('schedules.messages.deleteFailed'));
            }
        } catch (error) {
            alert(`${t('schedules.error.deleteError')}: ${error.message}`);
        }
    };

    const handleEditSchedule = (schedule) => {
        setEditingSchedule({
            ...schedule,
            shift_start: schedule.shift_start.split('T')[0] + 'T' + schedule.shift_start.split('T')[1].substring(0, 5),
            shift_end: schedule.shift_end.split('T')[0] + 'T' + schedule.shift_end.split('T')[1].substring(0, 5)
        });
        setEditMode(true);
    };

    const handleNewSchedule = () => {
        const today = localToday();
        setNewSchedule({
            user_id: parseInt(filters.operatorId) || '',
            shift_start: today + 'T08:00',
            shift_end: today + 'T16:00',
            status: 'SCHEDULED',
            machine_id: null,
            production_order_id: null,
            actual_start: null,
            actual_end: null,
            break_minutes: 0,
            overtime_minutes: 0,
            notes: ''
        });
        setEditMode(true);
    };

    const handleCancelEdit = () => {
        setEditingSchedule(null);
        setNewSchedule(null);
        setEditMode(false);
    };

    const handleUpdateField = (field, value) => {
        if (editingSchedule) {
            setEditingSchedule({ ...editingSchedule, [field]: value });
        } else if (newSchedule) {
            setNewSchedule({ ...newSchedule, [field]: value });
        }
    };

    const getScheduleTypeColor = (type) => {
        const colors = {
            SCHEDULED: '#2196f3',
            ACTIVE: '#4caf50',
            COMPLETED: '#757575',
            ABSENT: '#f44336',
            ON_BREAK: '#ff9800'
        };
        return colors[type] || '#757575';
    };

    const groupSchedulesByDate = () => {
        const grouped = {};
        schedules.forEach(schedule => {
            const date = schedule.shift_start.split('T')[0];
            if (!grouped[date]) {
                grouped[date] = [];
            }
            grouped[date].push(schedule);
        });

        // Sort schedules within each date
        Object.keys(grouped).forEach(date => {
            grouped[date].sort((a, b) => {
                if (a.operator_name !== b.operator_name) {
                    return (a.operator_name || '').localeCompare(b.operator_name || '');
                }
                return a.shift_start.localeCompare(b.shift_start);
            });
        });

        return grouped;
    };

    if (loading) {
        return <div className="operator-schedules-container">{t('schedules.loading')}</div>;
    }

    if (error) {
        return (
            <div className="operator-schedules-container">
                <div className="error-message">{error}</div>
            </div>
        );
    }

    const currentSchedule = editingSchedule || newSchedule;
    const groupedSchedules = groupSchedulesByDate();

    return (
        <div className="operator-schedules-container">
            {saveSuccess && (
                <div className="success-message" style={{ margin: '10px 20px' }}>{t('schedules.messages.saveSuccess')}</div>
            )}

            {/* Filters Section */}
            <div className="schedule-filters">
                <h3>{t('schedules.filters.title')}</h3>
                <div className="filter-grid">
                    <div className="filter-item">
                        <label>{t('schedules.filters.startDate')}</label>
                        <input
                            type="date"
                            value={filters.startDate}
                            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                            disabled={editMode && currentSchedule}
                        />
                    </div>
                    <div className="filter-item">
                        <label>{t('schedules.filters.endDate')}</label>
                        <input
                            type="date"
                            value={filters.endDate}
                            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                            disabled={editMode && currentSchedule}
                        />
                    </div>
                    <div className="filter-item">
                        <label>{t('schedules.filters.operator')}</label>
                        <select
                            value={filters.operatorId}
                            onChange={(e) => setFilters({ ...filters, operatorId: e.target.value })}
                            disabled={editMode && currentSchedule}
                        >
                            <option value="">{t('schedules.filters.allOperators')}</option>
                            {operators.map(operator => (
                                <option key={operator.user_id} value={operator.user_id}>
                                    {operatorName(operator)}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="filter-item">
                        <label>{t('schedules.filters.status')}</label>
                        <select
                            value={filters.scheduleType}
                            onChange={(e) => setFilters({ ...filters, scheduleType: e.target.value })}
                            disabled={editMode && currentSchedule}
                        >
                            <option value="">{t('schedules.filters.allStatus')}</option>
                            {scheduleTypes.map(type => (
                                <option key={type} value={type}>{statusLabel(type)}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Edit/New Schedule Form */}
            {editMode && currentSchedule && (
                <div className="edit-form-section">
                    <h3>{newSchedule ? t('schedules.editNew.newSchedule') : t('schedules.editNew.editSchedule')}</h3>
                    <div className="form-grid">
                        <div className="form-item">
                            <label>{t('schedules.form.fields.operator')}</label>
                            <select
                                value={currentSchedule.user_id}
                                onChange={(e) => handleUpdateField('user_id', parseInt(e.target.value))}
                            >
                                <option value="" disabled hidden>{t('schedules.form.selectOperator')}</option>
                                {operators.map(operator => (
                                    <option key={operator.user_id} value={operator.user_id}>
                                        {operatorName(operator)}
                                    </option>
                                ))}
                                {editingSchedule && editingSchedule.user_id &&
                                    !operators.some(o => o.user_id === editingSchedule.user_id) && (
                                    <option value={editingSchedule.user_id}>
                                        {editingSchedule.operator_name || editingSchedule.user_id}
                                    </option>
                                )}
                            </select>
                        </div>
                        <div className="form-item">
                            <label>{t('schedules.form.fields.machine')}</label>
                            <select
                                value={currentSchedule.machine_id || ''}
                                onChange={(e) => handleUpdateField('machine_id', e.target.value ? parseInt(e.target.value) : null)}
                            >
                                <option value="">{t('schedules.form.noMachine')}</option>
                                {machines.map(machine => (
                                    <option key={machine.machine_id} value={machine.machine_id}>
                                        {machine.machine_code ? `${machine.machine_code} - ${machine.machine_name}` : machine.machine_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-item">
                            <label>{t('schedules.form.fields.shiftStart')}</label>
                            <input
                                type="datetime-local"
                                value={currentSchedule.shift_start}
                                onChange={(e) => handleUpdateField('shift_start', e.target.value)}
                            />
                        </div>
                        <div className="form-item">
                            <label>{t('schedules.form.fields.shiftEnd')}</label>
                            <input
                                type="datetime-local"
                                value={currentSchedule.shift_end}
                                onChange={(e) => handleUpdateField('shift_end', e.target.value)}
                            />
                        </div>
                        <div className="form-item">
                            <label>{t('schedules.form.fields.status')}</label>
                            <select
                                value={currentSchedule.status}
                                onChange={(e) => handleUpdateField('status', e.target.value)}
                            >
                                {scheduleTypes.map(type => (
                                    <option key={type} value={type}>{statusLabel(type)}</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-item">
                            <label>{t('schedules.form.fields.breakMinutes')}</label>
                            <input
                                type="number"
                                min="0"
                                value={currentSchedule.break_minutes || 0}
                                onChange={(e) => handleUpdateField('break_minutes', parseInt(e.target.value) || 0)}
                            />
                        </div>
                        <div className="form-item">
                            <label>{t('schedules.form.fields.overtimeMinutes')}</label>
                            <input
                                type="number"
                                min="0"
                                value={currentSchedule.overtime_minutes || 0}
                                onChange={(e) => handleUpdateField('overtime_minutes', parseInt(e.target.value) || 0)}
                            />
                        </div>
                    </div>
                    <div className="form-item form-item-full">
                        <label>{t('schedules.form.fields.notes')}</label>
                        <textarea
                            value={currentSchedule.notes || ''}
                            onChange={(e) => handleUpdateField('notes', e.target.value)}
                            rows="3"
                            placeholder={t('schedules.form.placeholders.notes')}
                        />
                    </div>
                    <div className="form-actions" style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
                        <button onClick={handleCancelEdit} className="btn btn-secondary">{t('schedules.toolbar.cancel')}</button>
                    </div>
                </div>
            )}

            {/* Schedules List */}
            <div className="schedule-calendar-section">
                <h3 style={{ color: 'var(--primary)', marginBottom: '25px', marginTop: '10px', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600' }}>
                    {t('schedules.calendar')}
                </h3>
                {Object.keys(groupedSchedules).length === 0 ? (
                    <div className="empty-state">
                        <p>{t('schedules.noData.empty')}</p>
                        {!editMode && (
                            <button onClick={handleNewSchedule} className="btn btn-primary" style={{ marginTop: '15px' }}>
                                {t('schedules.noData.createFirst')}
                            </button>
                        )}
                    </div>
                ) : (
                    Object.keys(groupedSchedules).sort().map(date => (
                        <div key={date} className="schedule-date-group">
                            <div className="schedule-date-header">
                                <h4>
                                    {formatAppDate(new Date(date + 'T00:00:00'), {
                                        weekday: 'long',
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}
                                </h4>
                            </div>
                            <table className="schedule-table">
                                <thead>
                                    <tr>
                                        <th>{t('schedules.tableHeaders.time')}</th>
                                        <th>{t('schedules.tableHeaders.operator')}</th>
                                        <th>{t('schedules.tableHeaders.machine')}</th>
                                        <th>{t('schedules.tableHeaders.status')}</th>
                                        <th>{t('schedules.tableHeaders.breakMin')}</th>
                                        <th>{t('schedules.tableHeaders.otMin')}</th>
                                        <th>{t('schedules.tableHeaders.notes')}</th>
                                        {editMode && !currentSchedule && <th>{t('schedules.tableHeaders.actions')}</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {groupedSchedules[date].map(schedule => (
                                        <tr key={schedule.schedule_id}>
                                            <td>{formatAppDate(schedule.shift_start, 'time')} - {formatAppDate(schedule.shift_end, 'time')}</td>
                                            <td>{schedule.operator_name || tCommon('ui.unknown')}</td>
                                            <td>{schedule.machine_name || '-'}</td>
                                            <td>
                                                <span
                                                    className="schedule-status-badge"
                                                    style={{ backgroundColor: getScheduleTypeColor(schedule.status) }}
                                                >
                                                    {statusLabel(schedule.status)}
                                                </span>
                                            </td>
                                            <td>{schedule.break_minutes || 0}</td>
                                            <td>{schedule.overtime_minutes || 0}</td>
                                            <td>{schedule.notes || '-'}</td>
                                            {editMode && !currentSchedule && (
                                                <td>
                                                    <div className="action-buttons">
                                                        <button
                                                            onClick={() => handleEditSchedule(schedule)}
                                                            className="btn btn-secondary"
                                                            style={{ padding: '3px 6px', fontSize: '11px' }}
                                                        >
                                                            {t('schedules.actions.edit')}
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(schedule.schedule_id)}
                                                            className="btn btn-danger"
                                                            style={{ padding: '3px 6px', fontSize: '11px' }}
                                                        >
                                                            {t('schedules.actions.delete')}
                                                        </button>
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default OperatorSchedules;